import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import WorkflowStateModal from "@/components/WorkflowStateModal";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { WorkflowService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { WorkflowState, WorkflowStateInfo } from "@/utils/types/workflowMain";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Form } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkflowInfoForm from "./components/WorkflowInfoForm";
import WorkflowStatesTable from "./components/WorkflowStatesTable";

interface ValidationError {
  rowIndex: number;
  field: string;
  message: string;
}

const WorkflowFormPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = !!id;

  // State management
  const [workflowStates, setWorkflowStates] = useState<WorkflowState[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<WorkflowState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemovingState, setIsRemovingState] = useState<number | null>(null);

  // Data fetching queries
  const { data: workflow, isLoading } = useQuery({
    queryKey: ["workflow", id],
    queryFn: () => WorkflowService.getWorkflowById(id ?? ""),
    enabled: !!id,
  });

  // Load workflow data in edit mode
  useEffect(() => {
    if (isEditMode && workflow) {
      // Convert states_info to WorkflowState format
      if (workflow.states_info && Array.isArray(workflow.states_info)) {
        const convertedStates: WorkflowState[] = workflow.states_info.map(
          (stateInfo: WorkflowStateInfo) => ({
            state_id: stateInfo.state.id,
            action_id: stateInfo.action.id,
            next_state_id: stateInfo.next_state.id,
            role_id: stateInfo.role ?? "",
            user_id: stateInfo.user ?? "",
            due_day: stateInfo.due_day,
            condition: stateInfo.condition ?? "",
          })
        );
        setWorkflowStates(convertedStates);
      } else {
        setWorkflowStates([]);
      }
    }
  }, [isEditMode, workflow]);

  const transformWorkflow = (data: any) => {
    return {
      name: data.name,
      schema_name: data.schema_name,
      description: data.description,
      states: data.states_info.map((item: any) => ({
        state_id: item.state?.id ?? "",
        action_id: item.action?.id ?? "",
        next_state_id: item.next_state?.id ?? "",
        role_id: item.role ?? "",
        user_id: item.user ?? "",
        due_day: item.due_day ?? "",
      })),
    };
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    const formData = await form.validateFields();
    setIsSubmitting(true);

    // Clean workflow states by removing empty condition values
    const cleanedStates = workflowStates.map(state => {
      const { condition, ...rest } = state;
      return condition?.trim()
        ? { ...rest, condition: condition.trim() }
        : rest;
    });

    const workflowData = {
      ...formData,
      states: cleanedStates,
    };

    if (isEditMode && workflow) {
      // Check for changes before updating
      if (
        !checkFormChanges(workflowData, transformWorkflow(workflow), notify)
      ) {
        setIsSubmitting(false);
        return;
      }
      const response = await WorkflowService.updateWorkflow(
        workflow.id,
        workflowData
      );
      if (response) {
        navigate(navigationRoutes.WORKFLOW_LIST);
      }
    } else {
      const response = await WorkflowService.createWorkflow(workflowData);
      if (response) {
        navigate(navigationRoutes.WORKFLOW_LIST);
      }
    }
    setIsSubmitting(false);
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingIndex(null);
    setEditingData(null);
    setIsModalVisible(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setEditingData(workflowStates[index]);
    setIsModalVisible(true);
  };

  const handleModalSubmit = (data: WorkflowState) => {
    // Clean the data by removing empty condition values
    const cleanedData = { ...data };
    if (!cleanedData.condition?.trim()) {
      delete cleanedData.condition;
    } else {
      cleanedData.condition = cleanedData.condition.trim();
    }

    if (editingIndex !== null) {
      const updatedStates = [...workflowStates];
      updatedStates[editingIndex] = cleanedData;
      setWorkflowStates(updatedStates);
    } else {
      setWorkflowStates([...workflowStates, cleanedData]);
    }
    setIsModalVisible(false);
    setEditingData(null);
    setEditingIndex(null);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingIndex(null);
    setEditingData(null);
  };

  const removeWorkflowState = async (index: number) => {
    setIsRemovingState(index);
    const newStates = workflowStates.filter((_, i) => i !== index);
    setWorkflowStates(newStates);
    setValidationErrors(prev => prev.filter(error => error.rowIndex !== index));
    setIsRemovingState(null);
  };

  const isAnyActionInProgress =
    isLoading ?? isSubmitting ?? isRemovingState !== null;

  // Loading and error states
  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !workflow) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Workflow" : "Add Workflow"}
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmitForm}
              loading={isSubmitting}
              disabled={isAnyActionInProgress}
              title={isEditMode ? "Save Workflow Changes" : "Save New Workflow"}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <WorkflowInfoForm
        form={form}
        isEditMode={isEditMode}
        workflow={workflow}
        disabled={isAnyActionInProgress}
      />

      <WorkflowStatesTable
        workflowStates={workflowStates}
        validationErrors={validationErrors}
        onAddState={openAddModal}
        onEditState={openEditModal}
        onRemoveState={removeWorkflowState}
        disabled={isAnyActionInProgress}
        isRemovingState={isRemovingState}
      />

      {/* Workflow State Modal */}
      <WorkflowStateModal
        open={isModalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        initialData={editingData}
        isEditMode={editingIndex !== null}
        disabled={isAnyActionInProgress}
        confirmLoading={isAnyActionInProgress}
      />
    </Card>
  );
};

export default WorkflowFormPage;
